import React, { Fragment, useState } from "react";
import ScheduleFormik from "../Components/ScheduleComponents/ScheduleFormik";
import ScheduleFormView from "../Components/ScheduleComponents/ScheduleFormView";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import Api from "src/data/api";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { showNotification } from "src/redux/notificationActions";
import {
    EMPLOYEES_MESSAGES,
    ERROR_MESSAGES,
    SCHEDULE_MESSAGES,
} from "src/constants/messages";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";
import { compareAsc } from "date-fns";
import { fetchUsers } from "src/data/common";
const VIRTUAL_LOCATION = {
    value: 0,
    title: "Virtual",
};

const SCHEDULE_QUERY_KEYS = {
    userLocationSchedule: (userId, locationId) => [
        "users",
        "schedule",
        userId,
        locationId,
    ],
};

const USERS_LIST_KEY = ["users", "list"];

const fetchUserLocationSchedule = async (locationId, userId) => {
    const { data } = await Api.request({
        url: `/retailer/user/${userId}/schedule/${locationId}`,
    });

    return data.locationUserSchedule;
};

const managerUpdateUserLocationSchedule = async (data) => {
    await Api.request({
        method: "PUT",
        url: `/retailer/user/${data.userId}/schedule/${data.locationId}`,
        data: data.schedule,
    });
};

const sendInvitationToEmployee = async (data) => {
    await Api.request({
        method: "POST",
        url: "/retailer/emails/employee-email-invitation",
        data,
    });
};

const transformData = (data) => {
    data.daysBookedOff = data.daysOff
        .map((day) => {
            const parts = day.split("-");

            return new Date(
                Number(parts[0]),
                Number(parts[1]) - 1,
                Number(parts[2])
            );
        })
        .sort((a, b) => compareAsc(a, b));

    return data;
};

const UsersSchedules = ({ retailerLocations = [] }) => {
    const locationsDropdownValues = [
        ...retailerLocations
            .filter((location) => location.isVisible && location.isBillable)
            .map((location) => ({
                ...location,
                value: location.locationId,
                title: `${location.neighborhood} - ${location.address}`,
            })),
        VIRTUAL_LOCATION,
    ];

    const defLocation = locationsDropdownValues.length
        ? {
              value: locationsDropdownValues[0].locationId,
              title: `${locationsDropdownValues[0].neighborhood} - ${locationsDropdownValues[0].address}`,
          }
        : {};

    const currentDate = new Date();
    const dispatch = useDispatch();
    const user = useSelector((store) => store.user.data);
    const [currentUserId, setCurrentUserId] = useState(user.user_id);
    const [inviteEmployeeModalOpen, setInviteEmployeeModalOpen] =
        useState(false);

    const [employeeEmail, setEmployeeEmail] = useState("");
    const [employeeName, setEmployeeName] = useState("");

    const handleInviteEmployeeModalOpen = () =>
        setInviteEmployeeModalOpen(true);
    const handleInviteEmployeeModalClose = () => {
        setEmployeeEmail("");
        setEmployeeName("");
        setInviteEmployeeModalOpen(false);
    };

    const [currentLocation, setCurrentLocation] = useState(defLocation);

    const queryClient = useQueryClient();

    const { data: locationUserSchedule } = useQuery({
        queryKey: SCHEDULE_QUERY_KEYS.userLocationSchedule(
            currentUserId,
            currentLocation.value
        ),
        queryFn: () =>
            fetchUserLocationSchedule(currentLocation.value, currentUserId),
        enabled: !!currentUserId,
        select: transformData,
    });

    const { data: users } = useQuery({
        queryKey: USERS_LIST_KEY,
        queryFn: fetchUsers,
        placeholderData: [],
    });

    const managerUpdateScheduleMutation = useMutation(
        managerUpdateUserLocationSchedule,
        {
            onMutate: () => {
                // Snapshot the previous value
                const previousQueues = queryClient.getQueryData(
                    SCHEDULE_QUERY_KEYS.userLocationSchedule(
                        currentUserId,
                        currentLocation.value
                    )
                );
                // Return a context object with the snapshotted value
                return { previousQueues };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    SCHEDULE_QUERY_KEYS.userLocationSchedule(
                        currentUserId,
                        currentLocation.value
                    )
                );
                dispatch(
                    showNotification(
                        SCHEDULE_MESSAGES.UPDATE_SCHEDULE_SUCCESS,
                        NOTIFICATION_TYPES.SUCCESS,
                        NOTIFICATION_HEADERS.RETAILER_SCHEDULE
                    )
                );
            },
            onError: (err, data, context) => {
                queryClient.setQueryData(
                    SCHEDULE_QUERY_KEYS.userLocationSchedule(
                        currentUserId,
                        currentLocation.value
                    ),
                    context.previousQueues
                );
                dispatch(
                    showNotification(
                        ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                        NOTIFICATION_TYPES.ERROR,
                        NOTIFICATION_HEADERS.OOPS
                    )
                );
            },
        }
    );

    const inviteEmployeeMutation = useMutation(sendInvitationToEmployee, {
        onSuccess: () => {
            handleInviteEmployeeModalClose();
            dispatch(
                showNotification(
                    EMPLOYEES_MESSAGES.EMPLOYEE_INVITATION_SEND_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.EMPLOYEES
                )
            );
        },
        onError: () => {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        },
    });

    const handleManagerUpdateUserLocationSchedule = (data) => {
        managerUpdateScheduleMutation.mutate({
            locationId: currentLocation.value,
            userId: currentUserId,
            schedule: data,
        });
    };

    const handleSendInvitationToEmployee = (data) => {
        inviteEmployeeMutation.mutate(data);
    };

    return (
        <Fragment>
            <Button onClick={handleInviteEmployeeModalOpen}>
                add employee
            </Button>
            <Form.Group controlId="input-users">
                <Form.Control
                    value={currentUserId}
                    as="select"
                    custom
                    style={{ minWidth: 0, maxWidth: 400 }}
                    onChange={(e) => setCurrentUserId(e.target.value)}
                >
                    {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                            {user.email}
                        </option>
                    ))}
                </Form.Control>
            </Form.Group>
            <Form.Group controlId="input-location">
                <Form.Control
                    value={currentLocation.value}
                    as="select"
                    custom
                    style={{ minWidth: 0, maxWidth: 400 }}
                    onChange={(e) => {
                        const location = locationsDropdownValues.find(
                            (location) => location.value == e.target.value
                        );
                        setCurrentLocation(location);
                    }}
                >
                    {locationsDropdownValues.map((location) => (
                        <option key={location.value} value={location.value}>
                            {location.title}
                        </option>
                    ))}
                </Form.Control>
            </Form.Group>

            <ScheduleFormik
                schedule={locationUserSchedule}
                updateSchedule={handleManagerUpdateUserLocationSchedule}
            >
                {(props) => (
                    <ScheduleFormView {...props} currentDate={currentDate} />
                )}
            </ScheduleFormik>
            <Modal
                centered
                show={inviteEmployeeModalOpen}
                onHide={handleInviteEmployeeModalClose}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Invite employee</Modal.Title>
                </Modal.Header>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSendInvitationToEmployee({
                            email: employeeEmail,
                            name: employeeName,
                        });
                    }}
                >
                    <Modal.Body>
                        <Form.Group controlId="employee-name">
                            <Form.Label>Employee name</Form.Label>
                            <Form.Control
                                type="text"
                                name="employee-name"
                                value={employeeName}
                                placeholder="Bob"
                                required
                                onChange={(e) =>
                                    setEmployeeName(e.target.value)
                                }
                            />
                        </Form.Group>
                        <Form.Group controlId="employee-email">
                            <Form.Label>Employee email</Form.Label>
                            <Form.Control
                                type="email"
                                name="employee-email"
                                value={employeeEmail}
                                placeholder="example@example.com"
                                required
                                onChange={(e) =>
                                    setEmployeeEmail(e.target.value)
                                }
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="submit">Send Invitation</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Fragment>
    );
};

export default UsersSchedules;
